import React, { useEffect } from "react";
import LegalPage from "./LegalPage";
import useNoIndex from "./useNoIndex";

const Section = ({ title, children }) => (
  <section>
    <h2 className="mb-3 text-xl font-bold text-[#172033]">{title}</h2>
    <div className="space-y-3 text-[#3A4658]">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  useNoIndex();

  useEffect(() => {
    document.title = "Privacy Policy | CoursePivot";
  }, []);

  return (
    <LegalPage title="Privacy Policy" effectiveDate="9th August 2025">
      <Section title="1. Overview">
        <p>
          CoursePivot respects your privacy. This Privacy Policy explains how we
          collect, use, store, and protect information when you use
          CoursePivot.com, the CoursePivot dashboard, and our assignment writing
          services.
        </p>
        <p>
          By using our services, you agree to the practices described in this
          policy. For privacy questions, contact support@coursepivot.com.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <p>We may collect information that you provide directly, including:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Name, email address, phone number, and account details.</li>
          <li>Order instructions, uploaded files, messages, and support notes.</li>
          <li>Payment status, wallet activity, and transaction references.</li>
          <li>Feedback, complaints, revision requests, and service preferences.</li>
        </ul>
        <p>
          We may also collect technical information such as device, browser,
          login, cookie, and usage data needed to operate and secure the
          dashboard.
        </p>
      </Section>

      <Section title="3. How We Use Information">
        <p>We use information to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Create and manage your account.</li>
          <li>Process orders, payments, wallet top-ups, and refunds.</li>
          <li>Match orders with qualified writers and support staff.</li>
          <li>Provide customer support, revisions, and order updates.</li>
          <li>Improve service quality, security, and user experience.</li>
          <li>Comply with legal, financial, and fraud-prevention obligations.</li>
        </ul>
      </Section>

      <Section title="4. Confidentiality of Orders">
        <p>
          We treat your order details, uploaded files, chat messages, and
          personal information as confidential. Access is limited to team
          members and writers who need the information to complete or support
          your order.
        </p>
        <p>
          We do not sell your personal information. We do not publicly disclose
          your use of CoursePivot.
        </p>
      </Section>

      <Section title="5. Payments and Third-Party Providers">
        <p>
          Payments may be processed by trusted third-party payment providers.
          These providers may collect and process payment information under
          their own privacy and security policies.
        </p>
        <p>
          CoursePivot stores transaction references and payment status
          information needed to manage your orders, wallet, receipts, and
          support requests.
        </p>
      </Section>

      <Section title="6. Cookies and Analytics">
        <p>
          We may use cookies, local storage, and similar technologies to keep you
          signed in, remember preferences, protect your session, and understand
          how the dashboard is used.
        </p>
        <p>
          You can adjust browser settings to limit cookies, but some dashboard
          features may not work properly without them.
        </p>
      </Section>

      <Section title="7. Data Security">
        <p>
          We use reasonable administrative, technical, and organizational
          safeguards to protect account, order, and payment-related information.
          No online service can guarantee absolute security, but we work to
          reduce unauthorized access, misuse, and loss.
        </p>
      </Section>

      <Section title="8. Data Retention">
        <p>
          We keep information as long as needed to provide services, resolve
          disputes, maintain financial records, prevent fraud, comply with legal
          duties, and support account history. When information is no longer
          needed, we may delete or anonymize it.
        </p>
      </Section>

      <Section title="9. Your Choices">
        <p>
          You may update profile details through your dashboard. You may also
          contact us to request assistance with account access, privacy
          questions, or certain data requests.
        </p>
        <p>
          Some information may need to be retained where required for legal,
          payment, fraud-prevention, or dispute-resolution purposes.
        </p>
      </Section>

      <Section title="10. Contact Us">
        <p>
          If you have questions about this Privacy Policy or how CoursePivot
          handles your information, contact us at support@coursepivot.com.
        </p>
      </Section>
    </LegalPage>
  );
};

export default PrivacyPolicy;
